"use client";

import React, { useState, useMemo } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import {
  Info,
  Lightbulb,
  AlertTriangle,
  Bookmark,
  Check,
  Copy,
  Sparkles,
  Quote,
  CheckCircle2,
  XCircle,
  Bot,
  Terminal,
  ChevronDown,
} from "lucide-react";
import { CodeHighlight } from "./CodeHighlight";

interface Props {
  children: React.ReactNode;
  className?: string;
}

interface HeadingProps extends Props {
  id?: string;
}

import { slugify, slugifyText } from "@/lib/slugify";
export { slugify, slugifyText };

import { Hash } from "lucide-react";

/**
 * Large section heading (H2) inside a detail view
 */
export function TileHeadline({ children, className = "", id }: HeadingProps) {
  const headingId = id || slugify(children);
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!headingId) return;
    const url = `${window.location.origin}${window.location.pathname}#${headingId}`;
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    const el = document.getElementById(headingId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <h2
      id={headingId || undefined}
      className={`group scroll-mt-24 font-heading text-2xl font-black tracking-tight text-neutral-900 dark:text-white sm:text-3xl md:text-[2rem] mt-14 mb-4 pt-4 flex items-center justify-between sm:justify-start gap-3 ${className}`}
    >
      <span>{children}</span>
      {headingId && (
        <button
          type="button"
          onClick={handleCopy}
          className="relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-neutral-400 opacity-0 transition-all group-hover:opacity-100 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"
          aria-label={`Link zu Abschnitt ${String(children)} kopieren`}
          title="Abschnittslink kopieren"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-emerald-500" />
          ) : (
            <Hash className="h-3.5 w-3.5" />
          )}
          {copied && (
            <span className="absolute -top-7 left-1/2 -translate-x-1/2 rounded bg-neutral-900 px-2 py-0.5 text-[11px] font-medium text-white shadow-sm dark:bg-neutral-100 dark:text-neutral-900 whitespace-nowrap pointer-events-none">
              Kopiert!
            </span>
          )}
        </button>
      )}
    </h2>
  );
}

/**
 * Secondary heading (H3) inside a detail view
 */
export function TileSubheadline({
  children,
  className = "",
  id,
}: HeadingProps) {
  const headingId = id || slugify(children);
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!headingId) return;
    const url = `${window.location.origin}${window.location.pathname}#${headingId}`;
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    const el = document.getElementById(headingId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <h3
      id={headingId || undefined}
      className={`group scroll-mt-24 font-heading text-xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-2xl mt-8 mb-3 flex items-center justify-between sm:justify-start gap-2.5 ${className}`}
    >
      <span>{children}</span>
      {headingId && (
        <button
          type="button"
          onClick={handleCopy}
          className="relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-neutral-400 opacity-0 transition-all group-hover:opacity-100 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"
          aria-label={`Link zu Unterabschnitt ${String(children)} kopieren`}
          title="Abschnittslink kopieren"
        >
          {copied ? (
            <Check className="h-3 w-3 text-emerald-500" />
          ) : (
            <Hash className="h-3 w-3" />
          )}
          {copied && (
            <span className="absolute -top-7 left-1/2 -translate-x-1/2 rounded bg-neutral-900 px-2 py-0.5 text-[11px] font-medium text-white shadow-sm dark:bg-neutral-100 dark:text-neutral-900 whitespace-nowrap pointer-events-none">
              Kopiert!
            </span>
          )}
        </button>
      )}
    </h3>
  );
}

/**
 * Standard editorial body text with optimized measure (line length)
 */
export function TileText({ children, className = "" }: Props) {
  return (
    <p
      className={`font-sans text-base leading-relaxed text-neutral-700 dark:text-neutral-300 md:text-[1.0625rem] mb-5 last:mb-0 max-w-prose ${className}`}
    >
      {children}
    </p>
  );
}

/**
 * Lead / Abstract paragraph for section intros
 */
export function TileLead({ children, className = "" }: Props) {
  return (
    <p
      className={`font-sans text-lg font-normal leading-relaxed text-neutral-600 dark:text-neutral-400 md:text-xl mb-8 max-w-3xl ${className}`}
    >
      {children}
    </p>
  );
}

/**
 * Inline code pill with subtle styling
 */
export function TileInlineCode({ children, className = "" }: Props) {
  return (
    <code
      className={`rounded-md border border-neutral-200/80 bg-neutral-100/90 px-1.5 py-0.5 font-mono text-[0.85em] font-medium text-pink-600 dark:border-neutral-800 dark:bg-neutral-800/80 dark:text-pink-300 ${className}`}
    >
      {children}
    </code>
  );
}

/**
 * Code Block with macOS header, language tag, copy-to-clipboard button and real syntax highlighting
 */
export function TileCodeBlock({
  children,
  title,
  language,
  className = "",
  showLineNumbers,
}: Props & {
  title?: string;
  language?: string;
  showLineNumbers?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  // Extract raw string content for clipboard & highlighting
  const getRawText = (node: React.ReactNode): string => {
    if (typeof node === "string") return node;
    if (typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(getRawText).join("");
    if (
      React.isValidElement(node) &&
      (node.props as { children?: React.ReactNode })?.children
    ) {
      return getRawText((node.props as { children?: React.ReactNode }).children);
    }
    return "";
  };

  const rawCode = getRawText(children).replace(/\n$/, "");

  const handleCopy = () => {
    if (!rawCode) return;
    navigator.clipboard.writeText(rawCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const detectedLang = language
    ? language
    : title?.includes(".")
      ? title.split(".").pop()
      : undefined;

  const displayLang = detectedLang
    ? detectedLang.toUpperCase()
    : title
      ? "CODE"
      : null;

  return (
    <div
      className={`group relative my-6 overflow-hidden rounded-2xl border border-neutral-800/80 bg-zinc-950 shadow-xl dark:border-neutral-800/90 ${className}`}
    >
      {/* Code Window Header Bar */}
      <div className="flex items-center justify-between border-b border-zinc-800/80 bg-zinc-900/90 px-4 py-2.5 backdrop-blur-xs">
        <div className="flex items-center gap-2">
          {/* macOS window action dots */}
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-500/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
          </div>

          {title && (
            <span className="ml-2 font-mono text-xs font-medium text-zinc-300">
              {title}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {displayLang && (
            <span className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-zinc-400">
              {displayLang}
            </span>
          )}

          <button
            onClick={handleCopy}
            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-[11px] font-medium transition-all ${
              copied
                ? "bg-emerald-950/80 text-emerald-400 border border-emerald-800/50"
                : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
            }`}
            title="Code in Zwischenablage kopieren"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 text-emerald-400" />
                <span>Kopiert</span>
              </>
            ) : (
              <>
                <Copy className="h-3 w-3 opacity-70" />
                <span>Kopieren</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Code Body with Real Syntax Highlighting */}
      <CodeHighlight
        code={rawCode}
        language={detectedLang}
        showLineNumbers={showLineNumbers}
      />
    </div>
  );
}

/**
 * Semantic Callouts (info, tip, warning, note) with distinct accents and icons
 */
export type CalloutType = "info" | "tip" | "warning" | "note";

interface CalloutProps extends Props {
  type?: CalloutType;
  title?: string;
  icon?: React.ReactNode | string;
}

const CALLOUT_CONFIG: Record<
  CalloutType,
  {
    borderClass: string;
    bgClass: string;
    titleClass: string;
    defaultTitle: string;
    defaultIcon: React.ReactNode;
  }
> = {
  info: {
    borderClass: "border-sky-500/25 border-l-4 border-l-sky-500",
    bgClass: "bg-sky-50/60 dark:bg-sky-950/20",
    titleClass: "text-sky-900 dark:text-sky-300",
    defaultTitle: "Hinweis",
    defaultIcon: <Info className="h-4 w-4 text-sky-500" />,
  },
  tip: {
    borderClass: "border-emerald-500/25 border-l-4 border-l-emerald-500",
    bgClass: "bg-emerald-50/60 dark:bg-emerald-950/20",
    titleClass: "text-emerald-900 dark:text-emerald-300",
    defaultTitle: "Praxis-Tipp",
    defaultIcon: <Lightbulb className="h-4 w-4 text-emerald-500" />,
  },
  warning: {
    borderClass: "border-amber-500/30 border-l-4 border-l-amber-500",
    bgClass: "bg-amber-50/60 dark:bg-amber-950/20",
    titleClass: "text-amber-900 dark:text-amber-300",
    defaultTitle: "Achtung / Fallstrick",
    defaultIcon: <AlertTriangle className="h-4 w-4 text-amber-500" />,
  },
  note: {
    borderClass:
      "border-neutral-300/80 dark:border-neutral-700/80 border-l-4 border-l-neutral-500 dark:border-l-neutral-400",
    bgClass: "bg-neutral-50/90 dark:bg-neutral-900/50",
    titleClass: "text-neutral-900 dark:text-neutral-200",
    defaultTitle: "Notiz",
    defaultIcon: <Bookmark className="h-4 w-4 text-neutral-500" />,
  },
};

export function TileCallout({
  children,
  type = "info",
  title,
  icon,
  className = "",
}: CalloutProps) {
  const config = CALLOUT_CONFIG[type];
  const displayTitle = title ?? config.defaultTitle;
  const displayIcon =
    typeof icon === "string" ? (
      <span className="text-base">{icon}</span>
    ) : icon ? (
      icon
    ) : (
      config.defaultIcon
    );

  return (
    <div
      className={`my-6 rounded-2xl border p-5 md:p-6 shadow-xs backdrop-blur-xs transition-all ${config.borderClass} ${config.bgClass} ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">{displayIcon}</div>
        <div className="flex-1 min-w-0">
          {displayTitle && (
            <div
              className={`font-heading text-sm sm:text-base font-bold tracking-tight mb-1.5 ${config.titleClass}`}
            >
              {displayTitle}
            </div>
          )}
          <div className="font-sans text-sm md:text-[0.9375rem] leading-relaxed text-neutral-700 dark:text-neutral-300 [&>p]:mb-3 [&>p:last-child]:mb-0">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Highlighted Takeaway / Core Principle / Merksatz
 */
export function TileMerksatz({
  children,
  badge = "Merksatz",
  author,
  className = "",
}: Props & {
  badge?: string;
  author?: string;
}) {
  return (
    <div
      className={`relative my-8 overflow-hidden rounded-2xl border border-neutral-200 bg-gradient-to-br from-neutral-50/90 via-white to-neutral-100/60 p-6 md:p-7 shadow-xs dark:border-neutral-800 dark:from-neutral-900/90 dark:via-neutral-900/40 dark:to-neutral-950 ${className}`}
    >
      {/* Decorative background quotation icon */}
      <Quote className="pointer-events-none absolute -right-2 -bottom-3 h-24 w-24 text-neutral-200/50 dark:text-neutral-800/40" />

      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1 rounded-full border border-neutral-300/80 bg-white px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-neutral-800 shadow-xs dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
            <Sparkles className="h-2.5 w-2.5 text-amber-500" />
            {badge}
          </span>
        </div>

        <div className="font-heading text-lg sm:text-xl md:text-2xl font-bold tracking-tight text-neutral-900 dark:text-white leading-snug">
          {children}
        </div>

        {author && (
          <div className="mt-3 font-sans text-xs font-medium text-neutral-500 dark:text-neutral-400">
            — {author}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Compare component: Do's vs. Don'ts / Empfohlen vs. Vermeiden
 */
export function TileCompare({
  children,
  className = "",
}: Props) {
  return (
    <div className={`my-8 grid gap-4 md:grid-cols-2 ${className}`}>
      {children}
    </div>
  );
}

export function TileCompareDo({
  children,
  title = "Empfohlen / Best Practice",
  className = "",
}: Props & { title?: string }) {
  return (
    <div
      className={`rounded-2xl border border-emerald-500/30 bg-emerald-50/40 p-5 dark:border-emerald-500/25 dark:bg-emerald-950/20 ${className}`}
    >
      <div className="flex items-center gap-2 font-heading text-base font-bold text-emerald-900 dark:text-emerald-300 mb-2">
        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
        <span>{title}</span>
      </div>
      <div className="text-sm leading-relaxed text-neutral-700 dark:text-neutral-300 [&>ul]:list-disc [&>ul]:pl-4 [&>ul]:space-y-1">
        {children}
      </div>
    </div>
  );
}

export function TileCompareDont({
  children,
  title = "Zu vermeiden / Antipattern",
  className = "",
}: Props & { title?: string }) {
  return (
    <div
      className={`rounded-2xl border border-rose-500/30 bg-rose-50/40 p-5 dark:border-rose-500/25 dark:bg-rose-950/20 ${className}`}
    >
      <div className="flex items-center gap-2 font-heading text-base font-bold text-rose-900 dark:text-rose-300 mb-2">
        <XCircle className="h-4 w-4 text-rose-500 shrink-0" />
        <span>{title}</span>
      </div>
      <div className="text-sm leading-relaxed text-neutral-700 dark:text-neutral-300 [&>ul]:list-disc [&>ul]:pl-4 [&>ul]:space-y-1">
        {children}
      </div>
    </div>
  );
}

TileCompare.Do = TileCompareDo;
TileCompare.Dont = TileCompareDont;

/**
 * Standardized Interactive Playground Workbench Frame
 */
export function TilePlayground({
  children,
  label,
  title,
  description,
  status = "Interaktiv",
  actions,
  className = "",
}: Props & {
  label?: string;
  title?: string;
  description?: string;
  status?: string;
  actions?: React.ReactNode;
}) {
  const displayTitle = title || label || "Interaktiver Playground";

  return (
    <div
      className={`my-8 overflow-hidden rounded-2xl border border-neutral-200/90 bg-white/90 shadow-lg dark:border-neutral-800 dark:bg-neutral-900/80 backdrop-blur-md ${className}`}
    >
      {/* Workbench Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200/80 bg-neutral-50/90 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900/90">
        <div className="flex items-center gap-2.5">
          {/* Pulsing live dot */}
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>

          <span className="font-heading text-sm font-bold tracking-tight text-neutral-900 dark:text-white">
            {displayTitle}
          </span>

          {status && (
            <span className="rounded-full border border-neutral-200 bg-white px-2 py-0.5 font-mono text-[10px] font-semibold text-neutral-600 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
              {status}
            </span>
          )}
        </div>

        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {description && (
        <div className="border-b border-neutral-100 bg-white/50 px-5 py-2.5 text-xs text-neutral-500 dark:border-neutral-850 dark:bg-neutral-900/30 dark:text-neutral-400">
          {description}
        </div>
      )}

      {/* Workbench Inner Content */}
      <div className="p-4 sm:p-6">{children}</div>
    </div>
  );
}

/**
 * Grid container with responsive columns
 */
export function TileGrid({
  children,
  cols = 2,
  className = "",
}: Props & { cols?: 1 | 2 | 3 | 4 }) {
  const gridCols =
    cols === 1
      ? "grid-cols-1"
      : cols === 2
        ? "grid-cols-1 md:grid-cols-2"
        : cols === 3
          ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4";

  return (
    <div className={`my-6 grid gap-4 sm:gap-6 ${gridCols} ${className}`}>
      {children}
    </div>
  );
}

/**
 * Refined Card for displaying metrics, specs, or features
 */
export function TileCard({
  children,
  title,
  icon,
  badge,
  className = "",
}: Props & {
  title?: string;
  icon?: React.ReactNode;
  badge?: string;
}) {
  return (
    <div
      className={`group relative flex flex-col justify-between rounded-2xl border border-neutral-200/80 bg-white/80 p-5 sm:p-6 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:border-neutral-300 hover:bg-white hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900/50 dark:hover:border-neutral-700 dark:hover:bg-neutral-900 ${className}`}
    >
      <div>
        {(title || icon || badge) && (
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              {icon && <span className="text-neutral-500">{icon}</span>}
              {title && (
                <h4 className="font-heading text-base sm:text-lg font-bold tracking-tight text-neutral-900 dark:text-white">
                  {title}
                </h4>
              )}
            </div>
            {badge && (
              <span className="rounded border border-neutral-200 bg-neutral-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-neutral-600 dark:border-neutral-800 dark:bg-neutral-800 dark:text-neutral-400">
                {badge}
              </span>
            )}
          </div>
        )}
        <div className="font-sans text-sm sm:text-[0.9375rem] leading-relaxed text-neutral-600 dark:text-neutral-300">
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Mathematical Formula Display Component using KaTeX
 */
export interface FormulaVariable {
  symbol: string;
  meaning: string;
}

export interface TileFormulaProps {
  tex?: string;
  children?: React.ReactNode;
  title?: string;
  badge?: string;
  description?: string;
  variables?: FormulaVariable[];
  className?: string;
}

function RenderVariableSymbol({ symbol }: { symbol: string }) {
  const isLatex = /[_^\\{}]/.test(symbol);
  const html = useMemo(() => {
    if (!isLatex) return null;
    try {
      return katex.renderToString(symbol, {
        displayMode: false,
        throwOnError: false,
      });
    } catch {
      return null;
    }
  }, [symbol, isLatex]);

  if (html) {
    return <span dangerouslySetInnerHTML={{ __html: html }} className="inline-block" />;
  }

  return <span>{symbol}</span>;
}

export function TileFormula({
  tex: rawTex,
  children,
  title,
  badge = "Mathematisches Modell",
  description,
  variables,
  className = "",
}: TileFormulaProps) {
  const [copied, setCopied] = useState(false);

  const getRawText = (node: React.ReactNode): string => {
    if (typeof node === "string") return node;
    if (typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(getRawText).join("");
    if (
      React.isValidElement(node) &&
      (node.props as { children?: React.ReactNode })?.children
    ) {
      return getRawText((node.props as { children?: React.ReactNode }).children);
    }
    return "";
  };

  const tex = (rawTex || getRawText(children)).trim();

  const html = useMemo(() => {
    if (!tex) return null;
    try {
      return katex.renderToString(tex, {
        displayMode: true,
        throwOnError: false,
      });
    } catch {
      return null;
    }
  }, [tex]);

  const handleCopy = () => {
    if (!tex) return;
    navigator.clipboard.writeText(tex);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={`my-6 overflow-hidden rounded-2xl border border-neutral-200/90 bg-gradient-to-b from-neutral-50/90 via-white to-neutral-50/40 p-5 sm:p-6 shadow-xs dark:border-neutral-800 dark:from-neutral-900/90 dark:via-zinc-950 dark:to-neutral-900/40 ${className}`}
    >
      {(title || badge) && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200/60 pb-3 mb-4 dark:border-neutral-800/80">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-blue-500/20 bg-blue-500/10 px-2 py-0.5 font-mono text-[10px] font-semibold text-blue-600 dark:text-blue-400">
              <Sparkles className="h-2.5 w-2.5" />
              {badge}
            </span>
            {title && (
              <h4 className="font-heading text-sm sm:text-base font-bold text-neutral-900 dark:text-white">
                {title}
              </h4>
            )}
          </div>
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-[11px] font-medium text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 transition-colors"
            title="LaTeX-Code in Zwischenablage kopieren"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 text-emerald-500" />
                <span className="text-emerald-600 dark:text-emerald-400">Kopiert</span>
              </>
            ) : (
              <>
                <Copy className="h-3 w-3 opacity-70" />
                <span>LaTeX</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Rendered KaTeX Equation */}
      <div className="overflow-x-auto py-3 text-center text-neutral-900 dark:text-neutral-100 selection:bg-blue-500/20 font-serif">
        {html ? (
          <div
            dangerouslySetInnerHTML={{ __html: html }}
            className="inline-block text-base sm:text-lg md:text-xl"
          />
        ) : (
          <code className="font-mono text-sm">{tex}</code>
        )}
      </div>

      {description && (
        <p className="mt-3 text-xs sm:text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
          {description}
        </p>
      )}

      {variables && variables.length > 0 && (
        <div className="mt-4 pt-3 border-t border-neutral-200/50 dark:border-neutral-800/60">
          <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-2">
            Variablen & Parameter:
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
            {variables.map((v, i) => (
              <div key={i} className="flex items-baseline gap-2">
                <dt className="font-mono font-bold text-neutral-800 dark:text-neutral-200 shrink-0">
                  <RenderVariableSymbol symbol={v.symbol} />:
                </dt>
                <dd className="text-neutral-600 dark:text-neutral-400">
                  {v.meaning}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

/**
 * Inline Math Component for rendering KaTeX formulas within running text
 */
export function TileInlineMath({ tex }: { tex: string }) {
  const html = useMemo(() => {
    try {
      return katex.renderToString(tex, {
        displayMode: false,
        throwOnError: false,
      });
    } catch {
      return null;
    }
  }, [tex]);

  if (!html) return <code className="font-mono text-xs">{tex}</code>;

  return (
    <span
      dangerouslySetInnerHTML={{ __html: html }}
      className="inline-block px-0.5 align-baseline"
    />
  );
}

/**
 * Actionable AI Agent Prompt block for LLMs, Cursor, Claude Code, Antigravity
 */
export interface TileAgentPromptProps {
  children: React.ReactNode;
  title?: string;
  target?: string;
  badge?: string;
  description?: string;
  className?: string;
}

export function TileAgentPrompt({
  children,
  title = "Agent System Prompt",
  target,
  badge = "Agent Prompt",
  description,
  className = "",
}: TileAgentPromptProps) {
  const [copied, setCopied] = useState(false);

  const getRawText = (node: React.ReactNode): string => {
    if (typeof node === "string") return node;
    if (typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(getRawText).join("");
    if (
      React.isValidElement(node) &&
      (node.props as { children?: React.ReactNode })?.children
    ) {
      return getRawText((node.props as { children?: React.ReactNode }).children);
    }
    return "";
  };

  const rawPrompt = getRawText(children).trim();

  const handleCopy = () => {
    if (!rawPrompt) return;
    navigator.clipboard.writeText(rawPrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={`group relative my-6 overflow-hidden rounded-2xl border border-violet-500/25 bg-violet-50/40 shadow-md transition-all dark:border-violet-500/25 dark:bg-zinc-950 dark:shadow-xl ${className}`}
    >
      {/* Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-violet-200/80 bg-violet-100/70 px-4 py-3 backdrop-blur-xs dark:border-zinc-800/80 dark:bg-zinc-900/90">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="flex items-center gap-1.5 text-violet-600 dark:text-violet-400">
            <Bot className="h-4 w-4" />
            <span className="rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              {badge}
            </span>
          </div>

          <span className="font-heading text-sm font-bold tracking-tight text-neutral-900 dark:text-zinc-100">
            {title}
          </span>

          {target && (
            <span className="rounded border border-violet-200 bg-white/90 px-2 py-0.5 font-mono text-[11px] font-medium text-violet-800 dark:border-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-300">
              Ziel: {target}
            </span>
          )}
        </div>

        <button
          onClick={handleCopy}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-mono text-xs font-semibold transition-all cursor-pointer ${
            copied
              ? "bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-950/90 dark:text-emerald-400 dark:border-emerald-700/60"
              : "bg-violet-600/10 text-violet-700 border border-violet-300/80 hover:bg-violet-600/20 hover:text-violet-900 dark:bg-violet-600/20 dark:text-violet-200 dark:border-violet-500/40 dark:hover:bg-violet-600/30 dark:hover:text-white"
          }`}
          title="Prompt in Zwischenablage kopieren"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              <span>Prompt kopiert!</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5 opacity-80" />
              <span>Prompt kopieren</span>
            </>
          )}
        </button>
      </div>

      {description && (
        <div className="border-b border-violet-200/60 bg-violet-50/50 px-5 py-2.5 text-xs text-neutral-600 dark:border-zinc-800/60 dark:bg-zinc-900/40 dark:text-zinc-400">
          {description}
        </div>
      )}

      {/* Prompt Body */}
      <div className="overflow-x-auto p-4 sm:p-5 font-mono text-xs sm:text-sm leading-relaxed text-neutral-800 dark:text-zinc-200 selection:bg-violet-200 dark:selection:bg-violet-900/50 whitespace-pre-wrap [&_p]:text-neutral-800 dark:[&_p]:text-zinc-200 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ol]:text-neutral-800 dark:[&_ol]:text-zinc-200 [&_ol]:my-2.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1.5 [&_ul]:text-neutral-800 dark:[&_ul]:text-zinc-200 [&_ul]:my-2.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_li]:text-neutral-800 dark:[&_li]:text-zinc-200 [&_strong]:text-neutral-900 dark:[&_strong]:text-white [&_strong]:font-semibold [&_code]:rounded [&_code]:bg-violet-100/90 dark:[&_code]:bg-zinc-800/90 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-violet-900 dark:[&_code]:text-violet-300">
        {children}
      </div>
    </div>
  );
}

/**
 * Helper to detect and parse Markdown checkbox syntax: [ ] or [x]
 */
export function parseTaskItem(children: React.ReactNode): {
  isTask: boolean;
  defaultChecked: boolean;
  content: React.ReactNode;
} {
  const checkString = (str: string, rest: React.ReactNode[]) => {
    const match = str.match(/^\[([ xX])\]\s*(.*)$/);
    if (match) {
      const defaultChecked = match[1].toLowerCase() === "x";
      const remainder = match[2];
      const newChildren = remainder ? [remainder, ...rest] : rest;
      return { isTask: true, defaultChecked, content: newChildren };
    }
    return null;
  };

  const isInputCheckbox = (
    el: React.ReactNode,
  ): el is React.ReactElement<{
    checked?: boolean;
    defaultChecked?: boolean;
    type?: string;
  }> => {
    return (
      React.isValidElement(el) &&
      el.type === "input" &&
      (el.props as { type?: string })?.type === "checkbox"
    );
  };

  const cleanRest = (items: React.ReactNode[]) => {
    if (items.length > 0 && typeof items[0] === "string") {
      const trimmed = items[0].replace(/^\s+/, "");
      if (!trimmed) {
        return items.slice(1);
      }
      return [trimmed, ...items.slice(1)];
    }
    return items;
  };

  if (Array.isArray(children)) {
    if (isInputCheckbox(children[0])) {
      const defaultChecked = Boolean(
        children[0].props.checked || children[0].props.defaultChecked,
      );
      return {
        isTask: true,
        defaultChecked,
        content: cleanRest(children.slice(1)),
      };
    }

    if (typeof children[0] === "string") {
      const res = checkString(children[0], children.slice(1));
      if (res) return res;
    } else if (
      React.isValidElement(children[0]) &&
      (children[0].type === "p" || (children[0].props as any)?.children)
    ) {
      const pProps = children[0].props as { children?: React.ReactNode };
      const pChildren = Array.isArray(pProps?.children)
        ? pProps.children
        : [pProps?.children];

      if (isInputCheckbox(pChildren[0])) {
        const defaultChecked = Boolean(
          pChildren[0].props.checked || pChildren[0].props.defaultChecked,
        );
        return {
          isTask: true,
          defaultChecked,
          content: [
            ...cleanRest(pChildren.slice(1)),
            ...children.slice(1),
          ],
        };
      }

      if (typeof pChildren[0] === "string") {
        const res = checkString(pChildren[0], [
          ...pChildren.slice(1),
          ...children.slice(1),
        ]);
        if (res) return res;
      }
    }
  } else if (isInputCheckbox(children)) {
    return {
      isTask: true,
      defaultChecked: Boolean(
        children.props.checked || children.props.defaultChecked,
      ),
      content: null,
    };
  } else if (typeof children === "string") {
    const res = checkString(children, []);
    if (res) return res;
  } else if (React.isValidElement(children)) {
    const pProps = children.props as { children?: React.ReactNode };
    const pChildren = Array.isArray(pProps?.children)
      ? pProps.children
      : [pProps?.children];

    if (isInputCheckbox(pChildren[0])) {
      const defaultChecked = Boolean(
        pChildren[0].props.checked || pChildren[0].props.defaultChecked,
      );
      return {
        isTask: true,
        defaultChecked,
        content: cleanRest(pChildren.slice(1)),
      };
    }

    if (typeof pChildren[0] === "string") {
      const res = checkString(pChildren[0], pChildren.slice(1));
      if (res) return res;
    }
  }

  return { isTask: false, defaultChecked: false, content: children };
}

export function isTaskChild(child: React.ReactNode): boolean {
  if (!React.isValidElement(child)) return false;
  const props = child.props as { children?: React.ReactNode; className?: string };
  if (typeof props?.className === "string" && props.className.includes("task-list-item")) {
    return true;
  }
  return parseTaskItem(props?.children).isTask;
}

export function TileTaskItem({
  children,
  defaultChecked = false,
}: {
  children: React.ReactNode;
  defaultChecked?: boolean;
}) {
  const [checked, setChecked] = useState(defaultChecked);

  return (
    <li className="group flex items-start gap-3 my-2.5 list-none select-none">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={() => setChecked(!checked)}
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-all duration-200 cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500 ${
          checked
            ? "border-emerald-500 bg-emerald-500 text-white shadow-2xs dark:border-emerald-400 dark:bg-emerald-400 dark:text-neutral-950"
            : "border-neutral-300 bg-white hover:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:border-neutral-500"
        }`}
      >
        {checked && <Check className="h-3.5 w-3.5 stroke-[3]" />}
      </button>
      <div
        onClick={() => setChecked(!checked)}
        className={`flex-1 text-sm sm:text-base leading-relaxed transition-all duration-200 cursor-pointer ${
          checked
            ? "text-neutral-400 line-through dark:text-neutral-500"
            : "text-neutral-700 dark:text-neutral-300"
        }`}
      >
        {children}
      </div>
    </li>
  );
}

export function TileListItem({
  children,
  className = "",
  ...props
}: React.ComponentProps<"li">) {
  const parsed = parseTaskItem(children);
  const content = parsed.isTask ? parsed.content : children;
  return (
    <li className={`pl-1 ${className}`} {...props}>
      {content}
    </li>
  );
}

export function TileList({
  children,
  className = "",
  ...props
}: React.ComponentProps<"ul">) {
  return (
    <ul
      className={`my-4 list-disc space-y-1.5 pl-6 font-sans text-base leading-relaxed text-neutral-700 dark:text-neutral-300 ${className}`}
      {...props}
    >
      {children}
    </ul>
  );
}

/**
 * Collapsible Accordion Container (einklappbares Element)
 */
export interface TileCollapseProps {
  children: React.ReactNode;
  title: string;
  badge?: string;
  defaultOpen?: boolean;
  className?: string;
}

export function TileCollapse({
  children,
  title,
  badge,
  defaultOpen = false,
  className = "",
}: TileCollapseProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className={`my-6 overflow-hidden rounded-2xl border border-neutral-200/90 bg-neutral-50/60 transition-all dark:border-neutral-800 dark:bg-neutral-900/40 ${className}`}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="flex w-full min-h-[48px] items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-neutral-100/70 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500 dark:hover:bg-neutral-850/60 cursor-pointer select-none"
      >
        <div className="flex flex-wrap items-center gap-2.5">
          <div
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-neutral-200 bg-white transition-transform duration-200 dark:border-neutral-700 dark:bg-neutral-800 ${
              isOpen ? "rotate-180 text-violet-600 dark:text-violet-400" : "text-neutral-500"
            }`}
          >
            <ChevronDown className="h-4 w-4" />
          </div>

          <span className="font-heading text-sm sm:text-base font-bold tracking-tight text-neutral-900 dark:text-white">
            {title}
          </span>

          {badge && (
            <span className="rounded-full border border-neutral-200 bg-white px-2 py-0.5 font-mono text-[10px] font-semibold text-neutral-600 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
              {badge}
            </span>
          )}
        </div>

        <span className="shrink-0 font-mono text-xs text-neutral-400 dark:text-neutral-500">
          {isOpen ? "Einklappen" : "Ausklappen"}
        </span>
      </button>

      {isOpen && (
        <div className="border-t border-neutral-200/80 bg-white/70 p-4 sm:p-5 dark:border-neutral-800/80 dark:bg-neutral-950/60">
          {children}
        </div>
      )}
    </div>
  );
}


/**
 * Modern Interactive TileTable Component
 * Provides clean typography, responsive horizontal scrolling, sleek glassmorphism,
 * and high-contrast header & row hover styling.
 */
export interface TileTableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  children?: React.ReactNode;
  className?: string;
  wrapperClassName?: string;
  title?: string;
  subtitle?: string;
  badge?: string;
}

export function TileTable({
  children,
  className = "",
  wrapperClassName = "",
  title,
  subtitle,
  badge,
  ...props
}: TileTableProps) {
  return (
    <div
      className={`my-8 overflow-hidden rounded-2xl border border-neutral-200/90 bg-white/70 shadow-xs backdrop-blur-xs transition-all dark:border-neutral-800/90 dark:bg-neutral-900/50 ${wrapperClassName}`}
    >
      {(title || badge || subtitle) && (
        <div className="border-b border-neutral-200/80 bg-neutral-50/80 px-5 py-3.5 dark:border-neutral-800/80 dark:bg-neutral-850/60">
          <div className="flex items-center justify-between gap-3">
            {title && (
              <h4 className="font-heading text-sm sm:text-base font-bold tracking-tight text-neutral-900 dark:text-white">
                {title}
              </h4>
            )}
            {badge && (
              <span className="rounded-full border border-neutral-200 bg-white px-2 py-0.5 font-mono text-[10px] font-semibold text-neutral-600 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
                {badge}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
              {subtitle}
            </p>
          )}
        </div>
      )}
      <div className="relative overflow-x-auto [scrollbar-width:thin]">
        <table
          className={`w-full min-w-[580px] border-collapse text-left font-sans text-sm ${className}`}
          {...props}
        >
          {children}
        </table>
      </div>
    </div>
  );
}

export function TileTableHead({
  children,
  className = "",
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={`border-b border-neutral-200/90 bg-neutral-100/80 dark:border-neutral-700/80 dark:bg-neutral-900/95 dark:backdrop-blur-sm ${className}`}
      {...props}
    >
      {children}
    </thead>
  );
}

export function TileTableBody({
  children,
  className = "",
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody
      className={`divide-y divide-neutral-200/70 dark:divide-neutral-800/70 ${className}`}
      {...props}
    >
      {children}
    </tbody>
  );
}

export function TileTableRow({
  children,
  className = "",
  ...props
}: React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={`group transition-colors duration-150 hover:bg-neutral-100/60 dark:hover:bg-neutral-800/50 even:bg-neutral-50/40 dark:even:bg-neutral-900/40 ${className}`}
      {...props}
    >
      {children}
    </tr>
  );
}

export function TileTableHeaderCell({
  children,
  className = "",
  align,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement>) {
  const alignClass =
    align === "center"
      ? "text-center"
      : align === "right"
        ? "text-right"
        : "text-left";

  return (
    <th
      className={`px-4.5 py-3.5 text-xs font-bold uppercase tracking-wider text-neutral-700 dark:text-neutral-100 select-none ${alignClass} ${className}`}
      align={align}
      {...props}
    >
      {children}
    </th>
  );
}

export function TileTableCell({
  children,
  className = "",
  align,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  const alignClass =
    align === "center"
      ? "text-center"
      : align === "right"
        ? "text-right"
        : "text-left";

  return (
    <td
      className={`px-4.5 py-3.5 text-sm leading-relaxed text-neutral-700 dark:text-neutral-300 align-middle ${alignClass} ${className}`}
      align={align}
      {...props}
    >
      {children}
    </td>
  );
}

export { ExplainableInline } from "./ExplainableInline";
export type { ExplainableInlineProps, ExplainableColor } from "./ExplainableInline";



